/**
 * StudioRoleDashboard — dedicated per-role workspace.
 * Renders a focused dashboard for Editor / Designer / SMM / Creator.
 * Pulls from /api/studio/content/my/tasks and filters by stages relevant
 * to the role, surfacing role-specific KPIs at the top.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, AlertTriangle, Clock, CheckCircle2, Sparkles,
  Video, Image as ImageIcon, Hash, ScrollText, ChevronRight,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_CONFIG = {
  editor: {
    label: "Editor",
    icon: Video,
    eyebrow: "Editor workspace",
    intro: "Every clip waiting for your cut, across every channel.",
    stages: ["raw_uploaded", "editing", "edit_review"],
    primary_stage: "editing",
  },
  designer: {
    label: "Designer",
    icon: ImageIcon,
    eyebrow: "Designer workspace",
    intro: "Every thumbnail, banner & graphic in your queue.",
    stages: ["thumbnail", "thumbnail_review"],
    primary_stage: "thumbnail",
  },
  smm: {
    label: "SMM / Publisher",
    icon: Hash,
    eyebrow: "Social media workspace",
    intro: "Everything ready for SEO, scheduling and going live.",
    stages: ["seo", "scheduling", "scheduled"],
    primary_stage: "seo",
  },
  creator: {
    label: "Creator",
    icon: ScrollText,
    eyebrow: "Creator workspace",
    intro: "Ideas, scripts & recordings — your half of the pipeline.",
    stages: ["idea", "approved", "scripting", "script_review", "recording"],
    primary_stage: "scripting",
  },
};

const STAGE_LABEL = {
  idea: "Idea", approved: "Approved", scripting: "Scripting",
  script_review: "Script review", recording: "Recording",
  raw_uploaded: "Raw uploaded", editing: "Editing",
  edit_review: "Edit review", thumbnail: "Thumbnail",
  thumbnail_review: "Thumb review", seo: "SEO",
  scheduling: "Scheduling", scheduled: "Scheduled",
  published: "Published",
};

export default function StudioRoleDashboard() {
  const { role } = useParams();
  const navigate = useNavigate();
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.creator;
  const Icon = cfg.icon;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/studio/content/my/tasks`, { withCredentials: true })
      .then((r) => alive && setTasks(r.data?.tasks || []))
      .catch((err) => alive && setError(err?.response?.data?.detail || err.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [role]);

  const roleTasks = useMemo(
    () => tasks.filter((t) => cfg.stages.includes(t.stage)),
    [tasks, cfg]
  );

  const kpis = useMemo(() => ({
    queued: roleTasks.length,
    today: roleTasks.filter((t) => t.sla_days === 0).length,
    overdue: roleTasks.filter((t) => t.sla_days != null && t.sla_days < 0).length,
    in_review: roleTasks.filter((t) => t.stage?.endsWith("_review")).length,
  }), [roleTasks]);

  return (
    <>
      <Header />
      <div className="studio-shell">
        <button className="studio-back" onClick={() => navigate("/studio")} data-testid="role-back">
          <ArrowLeft size={14} /> Back to Studio
        </button>

        <motion.div className="studio-hero" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <span className="studio-eyebrow">
            <Icon size={12} /> {cfg.eyebrow}
          </span>
          <h1 className="studio-h1">
            {cfg.label} dashboard <span className="studio-h1-accent">— focused & cross-channel.</span>
          </h1>
          <p className="studio-sub">{cfg.intro}</p>

          <div className="studio-hero-cta" style={{ flexWrap: "wrap", gap: 8 }}>
            <Link to="/studio/role/editor" className="studio-btn-ghost" data-testid="role-nav-editor" data-active={role === "editor"}>
              <Video size={14}/> Editor
            </Link>
            <Link to="/studio/role/designer" className="studio-btn-ghost" data-testid="role-nav-designer" data-active={role === "designer"}>
              <ImageIcon size={14}/> Designer
            </Link>
            <Link to="/studio/role/smm" className="studio-btn-ghost" data-testid="role-nav-smm" data-active={role === "smm"}>
              <Hash size={14}/> SMM
            </Link>
            <Link to="/studio/role/creator" className="studio-btn-ghost" data-testid="role-nav-creator" data-active={role === "creator"}>
              <ScrollText size={14}/> Creator
            </Link>
          </div>
        </motion.div>

        {error && <div className="ac-error"><AlertTriangle size={16}/> {error}</div>}
        {loading && (
          <div className="studio-card studio-loading">
            <Loader2 size={22} className="ac-spin" />
            <p>Loading {cfg.label.toLowerCase()} queue…</p>
          </div>
        )}

        {!loading && (
          <>
            <div className="studio-ops-row" data-testid="role-kpis">
              <div className="studio-ops-card">
                <header><span className="studio-eyebrow">In queue</span></header>
                <p style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{kpis.queued}</p>
              </div>
              <div className="studio-ops-card">
                <header><span className="studio-eyebrow"><Clock size={12}/> Due today</span></header>
                <p style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{kpis.today}</p>
              </div>
              <div className="studio-ops-card">
                <header><span className="studio-eyebrow"><AlertTriangle size={12}/> Overdue</span></header>
                <p style={{ fontSize: 28, fontWeight: 800, margin: 0, color: kpis.overdue ? "#ef4444" : undefined }}>
                  {kpis.overdue}
                </p>
              </div>
              <div className="studio-ops-card">
                <header><span className="studio-eyebrow"><CheckCircle2 size={12}/> Awaiting review</span></header>
                <p style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{kpis.in_review}</p>
              </div>
            </div>

            <div className="studio-section-h">
              <h2>Your {cfg.label.toLowerCase()} queue</h2>
            </div>

            {roleTasks.length === 0 ? (
              <div className="studio-empty" data-testid="role-empty">
                <div className="studio-empty-icon"><Sparkles size={36} /></div>
                <h2>You&apos;re all clear 🎯</h2>
                <p>No {cfg.label.toLowerCase()} tasks assigned right now.</p>
              </div>
            ) : (
              <div className="studio-grid" data-testid="role-list">
                {roleTasks.map((t) => (
                  <motion.div
                    key={t._id}
                    className="studio-channel-card"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => navigate(`/studio/channels/${t.channel_id}?content=${t._id}`)}
                    data-testid={`role-task-${t._id}`}
                  >
                    <div className="studio-channel-head">
                      <div className="studio-channel-meta" style={{ flex: 1 }}>
                        <h3 style={{ fontSize: "1rem" }}>{t.title}</h3>
                        <p>{t.channel_title}</p>
                      </div>
                      <span className={`studio-role studio-role--${role}`}>
                        {STAGE_LABEL[t.stage] || t.stage}
                      </span>
                    </div>
                    <div className="studio-channel-foot">
                      <span className="studio-pill">
                        {t.sla_days == null ? "No due date"
                          : t.sla_days < 0 ? `Overdue ${Math.abs(t.sla_days)}d`
                          : t.sla_days === 0 ? "Due today"
                          : `${t.sla_days}d left`}
                      </span>
                      <span className="studio-go">Open <ChevronRight size={14}/></span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
